import { Node as TiptapNode, mergeAttributes, Editor } from '@tiptap/core';
import { GapCursor } from '@tiptap/pm/gapcursor';
import { Node } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react';
import {
  CustomSectionState,
  CustomSectionContentState,
  CustomSectionNodeType,
} from 'api/custom-section/types';
import { ComponentType } from 'react';
import { v4 } from 'uuid';
import {
  RlCustomSectionName,
  RlGroupSectionName,
} from '../custom-block-node-names';
import { AddCustomSectionPayload, RemoveCustomSectionPayload } from './types';
import { CustomSectionNodeOptions } from './types/extension-options';
import { CustomNodeView } from './ui/custom-node-view';
import { createCustomSectionTransactionFilter } from './utils/transaction-filter';

export { RlCustomSectionName } from '../custom-block-node-names';
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [RlCustomSectionName]: {
      addCustomSection: (payload: AddCustomSectionPayload) => ReturnType;
      removeCustomSection: (payload: RemoveCustomSectionPayload) => ReturnType;
      openCustomSection: (payload: { nodeAt: number }) => ReturnType;
      undoCustomSection: (payload: { nodeAt: number }) => ReturnType;
    };
  }
}

const isNodeEmpty = (node: Node) => {
  if (
    node.type.name === RlCustomSectionName &&
    node.attrs.contentState === CustomSectionContentState.Prompt
  ) {
    return false;
  }

  return node.content.size === 0;
};

const scrollInsertedNodeIntoView = (editor: Editor, insertPosition: number) => {
  setTimeout(() => {
    const node = editor.view.nodeDOM(insertPosition) as HTMLElement;

    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, 0);
};

const closeAllOpenSections = (tr: any) => {
  const { doc } = tr;

  // Walk through all nodes in the document
  doc.descendants((node: any, pos: number) => {
    if (
      node.type.name === RlCustomSectionName &&
      node.attrs.state === CustomSectionState.Open
    ) {
      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        state: CustomSectionState.Closed,
      });
    }
  });
};

export const RlCustomSection = TiptapNode.create<CustomSectionNodeOptions>({
  name: RlCustomSectionName,

  group: 'block',
  content: 'block*',

  // Keep only one instance of each property
  isolating: true,
  defining: true,
  draggable: true,
  selectable: false,
  allowGapCursor: true,

  marks: '',
  atom: false,

  addKeyboardShortcuts() {
    const deleteNodesInSelection = (editor: Editor): boolean => {
      const { state, view } = editor;
      const { selection } = state;

      if (selection.empty) {
        return false;
      }

      const { from, to } = selection;
      let deleteFrom = from;
      let deleteTo = to;
      let found = false;

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (
          node.type.name === RlCustomSectionName ||
          node.type.name === RlGroupSectionName
        ) {
          // Skip if the selection is entirely inside this node (it's a parent, not a sibling).
          // nodesBetween visits ancestor nodes too, so without this guard a text-only
          // selection within a custom section would expand the delete range to the whole node.
          if (pos < from && pos + node.nodeSize > to) {
            return false;
          }

          deleteFrom = Math.min(deleteFrom, pos);
          deleteTo = Math.max(deleteTo, pos + node.nodeSize);
          found = true;

          return false;
        }

        return true;
      });

      if (!found) {
        return false;
      }

      view.dispatch(state.tr.delete(deleteFrom, deleteTo));

      return true;
    };

    return {
      Backspace: ({ editor }) => {
        const { state, view } = editor;
        const { selection, doc } = state;
        const { empty, from } = selection;

        if (!empty) {
          return deleteNodesInSelection(editor);
        }

        const exactSelectionPosition = doc.resolve(from - 1);
        const node = exactSelectionPosition.node();

        if (node == null || node.type.name !== this.name) {
          return false;
        }

        const atStartOfNode = exactSelectionPosition.parentOffset === 0;
        const atEndOfNode =
          exactSelectionPosition.parentOffset === node.content.size;

        if (atStartOfNode) {
          const beforeNodePos = exactSelectionPosition.before();
          const $beforeNode = doc.resolve(beforeNodePos);
          const nodeBefore = $beforeNode.nodeBefore;

          if (nodeBefore && nodeBefore.type.name === this.name) {
            view.dispatch(
              state.tr.setSelection(new GapCursor(doc.resolve(beforeNodePos))),
            );

            return true;
          }

          if (nodeBefore) {
            const endOfNodeBeforePos = beforeNodePos - 1;

            if (endOfNodeBeforePos >= 0) {
              view.dispatch(
                state.tr.setSelection(
                  TextSelection.near(doc.resolve(endOfNodeBeforePos)),
                ),
              );

              return true;
            }
          }

          return false;
        }

        if (atEndOfNode) {
          return editor.commands.removeCustomSection({
            nodeAt: exactSelectionPosition.before(),
          });
        }

        return false;
      },
      Delete: ({ editor }) => deleteNodesInSelection(editor),
    };
  },

  addCommands() {
    return {
      addCustomSection:
        (payload: AddCustomSectionPayload) =>
        ({ editor, tr }) => {
          // Close all other open sections before adding a new one
          closeAllOpenSections(tr);

          const customSectionNode = editor.schema.nodeFromJSON({
            type: this.name,
            attrs: {
              ...payload.attributes,
              templateSectionId:
                payload.attributes.templateSectionId ??
                (this.options.generateTemplateSectionId ? v4() : null),
            },
            content: payload.content ?? '',
          });
          const insertPos = payload.insertAt ?? tr.selection.from;
          const $from = tr.doc.resolve(insertPos);

          if ($from.depth === 0) {
            const docNode = $from.node();

            if (
              docNode.childCount === 1 &&
              docNode.child(0).type.name === 'paragraph' &&
              docNode.child(0).content.size === 0
            ) {
              const emptyParagraphPos = $from.start(0);

              tr.replaceRangeWith(
                emptyParagraphPos,
                emptyParagraphPos + docNode.child(0).nodeSize,
                customSectionNode,
              );
              scrollInsertedNodeIntoView(editor, emptyParagraphPos);

              return true;
            }
            tr.insert($from.pos, customSectionNode);
            scrollInsertedNodeIntoView(editor, $from.pos);

            return true;
          }

          const parentNode = $from.node(1);

          if (parentNode.type.name === CustomSectionNodeType.RlGroupSection) {
            if (
              parentNode.childCount === 1 &&
              parentNode.child(0).type.name === 'paragraph' &&
              parentNode.child(0).content.size === 0
            ) {
              const emptyParagraphPos = $from.start(1) + 1;

              tr.replaceRangeWith(
                emptyParagraphPos,
                emptyParagraphPos + parentNode.child(0).nodeSize - 1,
                customSectionNode,
              );
              scrollInsertedNodeIntoView(editor, emptyParagraphPos);

              return true;
            }
            tr.insert(insertPos, customSectionNode);
            scrollInsertedNodeIntoView(editor, insertPos);

            return true;
          }

          if (isNodeEmpty(parentNode)) {
            const fromPos = $from.start(1);
            const toPos = $from.end(1);

            tr.replaceRangeWith(
              fromPos,
              Math.min(toPos, editor.state.doc.content.size),
              customSectionNode,
            );
            scrollInsertedNodeIntoView(editor, fromPos);

            return true;
          }

          tr.insert(
            Math.min($from.end(1) + 1, editor.state.doc.content.size),
            customSectionNode,
          );
          scrollInsertedNodeIntoView(editor, $from.end(1) + 1);

          return true;
        },
      removeCustomSection:
        (payload: RemoveCustomSectionPayload) =>
        ({ tr }) => {
          const node = tr.doc.nodeAt(payload.nodeAt);

          if (node) {
            tr.deleteRange(payload.nodeAt, payload.nodeAt + node.nodeSize);
          }

          return true;
        },
      openCustomSection:
        (payload: { nodeAt: number }) =>
        ({ editor, tr }) => {
          // Close all other open sections first
          closeAllOpenSections(tr);

          const node = tr.doc.nodeAt(payload.nodeAt);

          if (node && node.type.name === this.name) {
            tr.setNodeMarkup(payload.nodeAt, undefined, {
              ...node.attrs,
              state: CustomSectionState.Open,
            });

            // Scroll the opened section to center of screen
            setTimeout(() => {
              const nodeElement = editor.view.nodeDOM(
                payload.nodeAt,
              ) as HTMLElement;

              if (nodeElement) {
                nodeElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'center',
                });
              }
            }, 0);
          }

          return true;
        },
      undoCustomSection:
        (payload: { nodeAt: number }) =>
        ({ tr }) => {
          const node = tr.doc.nodeAt(payload.nodeAt);

          if (node && node.type.name === this.name) {
            // Close the section without saving any changes
            tr.setNodeMarkup(payload.nodeAt, undefined, {
              ...node.attrs,
              state: CustomSectionState.Closed,
            });
          }

          return true;
        },
    };
  },
  addOptions() {
    return {
      allowResolve: false,
      generateTemplateSectionId: false,
      derivedSectionSourcesUpdater: undefined,
      validPositions: {
        beforeMatch: ['paragraph'],
        afterMatch: ['paragraph'],
      },
      allowsNesting: false,
      pageType: undefined,
    };
  },
  addAttributes() {
    return {
      templateSectionId: {
        default: null,
        parseHTML: (element) => {
          const value =
            element.getAttribute('data-template-section-id') ??
            element.getAttribute('data-uuid');

          return value ? Number(value) : null;
        },
        renderHTML: (attributes) => {
          return {
            ['data-template-section-id']: attributes.templateSectionId,
          };
        },
      },
      state: {
        default: CustomSectionState.Closed,
      },
      contentState: {
        default: CustomSectionContentState.Generated,
        parseHTML: (element) => {
          return (
            element.getAttribute('data-content-state') ??
            element.getAttribute('data-component-type')
          );
        },
        renderHTML: (attributes) => {
          return {
            ['data-content-state']: attributes.contentState,
          };
        },
      },
      sectionType: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute('data-section-type');
        },
        renderHTML: (attributes) => {
          return {
            ['data-section-type']: attributes.sectionType,
          };
        },
      },
      isTableStatic: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-table-static') === 'true',
        renderHTML: (attrs) =>
          attrs.isTableStatic ? { 'data-table-static': 'true' } : {},
      },
      title: {
        default: null,
      },
      config: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('data-config');

          if (!raw) {
            return null;
          }
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        },
        renderHTML: (attrs) =>
          attrs.config ? { 'data-config': JSON.stringify(attrs.config) } : {},
      },
      response: {
        default: null,
      },
      documentSectionId: {
        default: null,
      },
      tempId: {
        default: null,
      },
      editable: {
        default: true,
      },
      signalType: {
        default: 'all',
      },
      signalsSelected: {
        default: [],
      },
      filters: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `div[data-type="${this.name}"]`,
        getAttrs: (node) => {
          if (!node.getAttribute('data-section-type')) {
            return false;
          }

          return null;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        ['data-type']: this.name,
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      CustomNodeView as unknown as ComponentType<
        ReactNodeViewProps<HTMLElement>
      >,
      {
        as: 'section',
        className: 'custom-section',
        attrs: ({ node }) => {
          return {
            ['data-content-state']: node.attrs.contentState.toLowerCase(),
            ['data-section-type']: node.attrs.sectionType,
            ['data-template-section-id']: node.attrs.templateSectionId,
            draggable: 'true',
            style: 'cursor: grab',
          };
        },
      },
    );
  },

  topNode: true,

  addProseMirrorPlugins() {
    return [
      createCustomSectionTransactionFilter({
        nodeName: this.name,
        validPositions: this.options.validPositions,
        allowedNeighborNodes: [RlGroupSectionName],
      }),
    ];
  },
});
